import { KeyboardEventHandler, useEffect, useState } from "react";
import { BoldIcon, ListIcon, ListTodoIcon } from "lucide-react";
import {
  EditorContent,
  Extension,
  generateHTML,
  generateJSON,
  JSONContent,
  useEditor,
} from "@tiptap/react";
import Placeholder from "@tiptap/extension-placeholder";

import { Row } from "@/components/Row";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { tiptap } from "@/lib/tiptap";
import { cn } from "@/lib/utils";

const jsonToHtml = tiptap.jsonToHtmlFn();

// TODO [Low][LAB-331]: Integrate with Input component.

export function RichTextEditorInput({
  autoFocus,
  className,
  containerClassName,
  disabled,
  disableBold,
  editorContentClasses,
  editorHeight,
  extensions,
  infoHint,
  mode,
  onBlur,
  onFocus,
  onKeyDown,
}: {
  autoFocus?: boolean;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  disableBold?: boolean;
  editorContentClasses?: string;
  editorHeight?: number | string;
  extensions?: Extension[];
  infoHint?: string;
  mode:
    | {
        type: "html";
        onChange: (value: string) => void;
        value: string;
      }
    | {
        type: "json";
        onChange: (value: JSONContent) => void;
        value: JSONContent;
      };
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: KeyboardEventHandler;
}) {
  const { onChange, type, value } = mode;
  const [isFocused, setIsFocused] = useState(false);

  const infoHintLines = infoHint?.split("\n").filter(Boolean);
  const placeholderText = infoHintLines
    ? infoHintLines.map((line) => `· ${line.trim()}`).join("\n")
    : undefined;

  const editor = useEditor({
    // NOTE: When adding or removing an extension, the `SanitizeHtmlService` needs
    // to be updated on the 🥑 side otherwise the editor may not work as expected.
    extensions: [
      ...(extensions ?? tiptap.generateBaseExtensions({ disableBold })),
      ...(placeholderText
        ? [
            Placeholder.configure({
              placeholder: placeholderText,
            }),
          ]
        : []),
    ],
    onFocus: () => {
      setIsFocused(true);
      onFocus?.();
    },
    onBlur: () => {
      setIsFocused(false);
      onBlur?.();
    },
    onUpdate: ({ editor }) => {
      // TODO: Ensure no extra renders.

      if (type === "html") {
        // NOTE: When the editor is empty (e.g. <p></p>), we need to set the value to an empty string.
        if (editor.getHTML() === "<p></p>") {
          onChange("");
        } else {
          onChange(editor.getHTML());
        }
      } else {
        // TODO: See if we need to do something similar to the `html` type with respect to empty content.
        onChange(editor.getJSON());
      }
    },
  });

  // Used to sync the incoming `value` into the editor (initial value, record
  // switch, external form reset).
  //
  // Both sides of the comparison are round-tripped through the editor schema
  // so parsing artifacts cancel out: entity escaping (`&` vs `&amp;`) and
  // whitespace collapsing (a trailing space the DOM parser strips) would
  // otherwise make a raw compare against `editor.getHTML()` mismatch on every
  // keystroke, firing `setContent` and eating the caret/spaces. With both
  // sides normalized identically, the sync only fires on a genuine external
  // change, so it is safe to run even while the editor is focused — required
  // for `form.reset()` to clear the editor after submitting a comment.
  useEffect(() => {
    if (!editor) {
      return;
    }
    const editorExtensions =
      extensions ?? tiptap.generateBaseExtensions({ disableBold });
    const normalize = (html: string) => {
      // An empty editor reports "<p></p>", while an empty external value is "".
      if (html === "") {
        return "<p></p>";
      }
      return generateHTML(generateJSON(html, editorExtensions), editorExtensions);
    };
    const incomingHtml = type === "json" ? jsonToHtml(value) : value;
    if (normalize(incomingHtml) !== normalize(editor.getHTML())) {
      editor.commands.setContent(value, false);
    }
  }, [editor, type, value, extensions, disableBold]);

  // Used to disable the editor when the disabled prop is true.
  useEffect(() => {
    if (disabled && editor) {
      editor.commands.setContent("");
      editor.setEditable(false);
    } else if (!disabled && editor) {
      editor.setEditable(true);
    }
  }, [disabled, editor]);

  // Move focus into the editor as soon as it is mounted and ready.
  useEffect(() => {
    if (autoFocus && editor) {
      editor.commands.focus("end");
    }
  }, [autoFocus, editor]);

  const showHintBox = isFocused && !!infoHintLines;

  return (
    <div>
      <div
        className={cn("rounded-lg border border-slate-200", containerClassName)}
      >
        <Row className="items-center gap-2 border-b border-slate-200 p-2">
          {!disableBold && (
            <button
              disabled={disabled}
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Bold",
                  getScreenFromPathname(location.pathname),
                );
                editor?.chain().focus().toggleBold().run();
              }}
              // Needed to avoid submitting the form.
              type="button"
            >
              <BoldIcon
                className={cn(
                  "h-4 w-4 text-slate-400",
                  editor?.isActive("bold") && "text-slate-900",
                )}
              />
            </button>
          )}
          <button
            disabled={disabled}
            onClick={() => {
              mixpanelUtils.trackButtonClick(
                "Bullet list",
                getScreenFromPathname(location.pathname),
              );
              editor?.chain().focus().toggleBulletList().run();
            }}
            // Needed to avoid submitting the form.
            type="button"
          >
            <ListIcon
              className={cn(
                "h-5 w-5 text-slate-400",
                editor?.isActive("bulletList") && "text-slate-900",
              )}
            />
          </button>
          <button
            disabled={disabled}
            onClick={() => {
              mixpanelUtils.trackButtonClick(
                "Task list",
                getScreenFromPathname(location.pathname),
              );
              editor?.chain().focus().toggleTaskList().run();
            }}
            // Needed to avoid submitting the form.
            type="button"
          >
            <ListTodoIcon
              className={cn(
                "h-5 w-5 text-slate-400",
                editor?.isActive("taskList") && "text-slate-900",
              )}
            />
          </button>
        </Row>
        <EditorContent
          className={cn(
            "overflow-y-auto",
            className,
            editorContentClasses,
            disabled && "cursor-not-allowed",
          )}
          disabled={disabled}
          editor={editor}
          // Needed to allow the editor line to be focused when the user clicks anywhere in the display area.
          onClick={() => editor?.commands.focus()}
          onKeyDown={onKeyDown}
          style={{ height: editorHeight }}
        />
      </div>
      {showHintBox && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <ul className="space-y-1 text-sm text-slate-400">
            {infoHintLines.map((line, i) => (
              <li key={i} className="flex gap-2">
                <span>·</span>
                <span>{line.trim()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
